import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { FirebaseContext } from '../Firebase';
import { AuthUserContext } from '../Session';
import * as ROUTES from '../../constants/routes';
import './Blog.css';

const ArticlePage = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug, currentLang]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const data = await firebase.doGetArticle(slug);
      setArticle(data);
      
      // Track view
      if (data) {
        firebase.doIncrementArticleViews(slug).catch(err => 
          console.log('View tracking failed:', err)
        );
        
        // Track article visit in analytics
        if (firebase.analytics) {
          firebase.analytics.logEvent('article_view', {
            article_title: data.translations?.en?.title || data.title,
            article_slug: slug,
            article_author: data.author || 'Unknown',
            article_tags: data.tags?.join(',') || 'None',
            language: currentLang,
            device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            is_authenticated: !!authUser,
            referrer: document.referrer || 'direct',
            timestamp: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error loading article:', error);
    }
    setLoading(false);
  };
// Public access: all users can view published articles
  if (!article && !loading) {
    return (
      <div className="article-not-found">
        <h1>{t('blog.not_found','Article not found')}</h1>
        <Link to={ROUTES.BLOG}>← {t('blog.back_to_blog', 'Back to Blog')}</Link>
      </div>
    );
  }
  
  if (loading) return <div className="loading">{t('loading')}</div>;

  // Get translation for current language or fallback to English
  const translation = article.translations?.[currentLang] || article.translations?.en || {};

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{translation.title} | Replan Construction</title>
        <meta name="description" content={translation.excerpt} />
        <meta property="og:title" content={translation.title} />
        <meta property="og:description" content={translation.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://replan360.com/blog/${article.slug}`} />
        {article.featuredImage && (
          <meta property="og:image" content={article.featuredImage} />
        )}
        <meta name="keywords" content={article.tags?.join(', ')} />
        <link rel="canonical" href={`https://replan360.com/blog/${article.slug}`} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": translation.title,
            "description": translation.excerpt,
            "author": {
              "@type": "Person",
              "name": article.author || "Replan Construction"
            },
            "datePublished": article.publishedAt?.toDate ? article.publishedAt.toDate().toISOString() : new Date().toISOString(),
            "dateModified": article.updatedAt?.toDate ? article.updatedAt.toDate().toISOString() : new Date().toISOString(),
            "keywords": article.tags?.join(', '),
            "inLanguage": currentLang
          })}
        </script>
      </Helmet>

      <article className="article-full" >
        <Link to={ROUTES.BLOG} className="back-link">← {t('blog.back_to_blog', 'Back to Blog')}</Link>
        
        <header>
          <h1>{translation.title}</h1>
          <div >
            <time dateTime={article.publishedAt?.toDate ? article.publishedAt.toDate().toISOString() : ''}>
              {article.publishedAt?.toDate ? new Date(article.publishedAt.toDate()).toLocaleDateString(currentLang, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : t('blog.recently','Recently')}
            </time>
            {/* {article.author && <span> • {t('blog.by', 'By')} {article.author}</span>}
            {article.views && <span> • {article.views} {t('blog.views', 'views')}</span>} */}
          </div>
          {translation.tags && translation.tags.length > 0 && (
            <div className="article-tags">
              {translation.tags.map(tag => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          )}
        </header>

        {article.featuredImage && (
          <img 
            src={article.featuredImage} 
            alt={translation.title}
            className="featured-image"
          />
        )}

        {/* Display table of contents (excerpt) if available */}
        {translation.excerpt && (
          <div 
            className="article-excerpt-toc"
            dangerouslySetInnerHTML={{ __html: translation.excerpt }}
          />
        )}

        <div 
          
          dangerouslySetInnerHTML={{ __html: translation.content }}
        />
      </article>
    </>
  );
};

export default ArticlePage;
